import { memoize } from 'lodash'

import { GATE_TO_PERMISSION } from '@acre/utils'
import {
  PermissionAssignmentName,
  PermissionGateName,
  PermissionResolutionAssigneeInput,
  PermissionResolutionInput,
  PermissionScopeType,
  ResolvedPermissionResolution,
} from '@acre/graphql'

import {
  GeneratePermissionResolutionInputProps,
  PermissionBuilderProps,
  PermissionInput,
  PermissionInputWithAssignees,
} from './permission.types'

// certain gates can be conditionally excluded from permission resolution requests
export const excludeInputsBasedOnGates = ({
  gateExceptions,
  inputs,
}: {
  gateExceptions: PermissionGateName[]
  inputs: PermissionInput[] | PermissionInputWithAssignees[]
}) => {
  return [...inputs].reduce((acc: PermissionInput[], input: PermissionInput) => {
    let newInput = { ...input }

    if (Array.isArray(newInput.gates)) {
      newInput = {
        ...newInput,
        // remove the gates which are excepted
        gates: newInput.gates.filter((gate) => !gateExceptions.includes(gate)) || [],
      }
    }
    // if no gates are left then do not preserve the input
    if (!newInput.gates.length) {
      return [...acc]
    }
    return [...acc, newInput]
  }, [])
}

const PermissionAssignmentNamesWithoutScope = [
  PermissionAssignmentName.ORGANISATION_CREATE_INTRODUCER_ORG,
  PermissionAssignmentName.REGULATED_MORTGAGE_PROVISIONAL,
  PermissionAssignmentName.REGULATED_MORTGAGE,
  PermissionAssignmentName.REGULATED_EQUITYRELEASE_PROVISIONAL,
  PermissionAssignmentName.REGULATED_EQUITYRELEASE,
  PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE_PROVISIONAL,
  PermissionAssignmentName.REGULATED_PROTECTIONINSURANCE,
  PermissionAssignmentName.REGULATED_GENERALINSURANCE_PROVISIONAL,
  PermissionAssignmentName.REGULATED_GENERALINSURANCE,
  PermissionAssignmentName.ACRE_ORGANISATION_CREATE_REG_ORG,
]

/**
 * Function to create all possible permutations of permission and assignees
 * @param permissionResolutionInputs permission resolutions which can include the source (but not necessarily)
 * @param assignees (assignees that you would like to check permissions for)
 * @returns permissions resolutions for every assignee specified
 */
const getPermissionsInputsForEveryAssignee = (
  permissionResolutionInputs: PermissionResolutionInput[],
  assignees?: PermissionResolutionAssigneeInput[],
) => {
  if (assignees && assignees.length) {
    return assignees.reduce((acc, assignee) => {
      const assigneesPermissionResolution = permissionResolutionInputs.map((resolution) => ({
        ...resolution,
        assignee,
      }))

      return [...acc, ...assigneesPermissionResolution]
    }, [] as PermissionResolutionInput[])
  }

  return permissionResolutionInputs
}

export const generatePermissionResolutionInput = ({
  entityIdsCollection = {},
  permissionName,
  assignees,
}: GeneratePermissionResolutionInputProps): PermissionResolutionInput[] => {
  if (!permissionName) return []
  let permissionResolutionInput: PermissionResolutionInput[] = []

  const entityIdsCollectionKeys = Object.keys(entityIdsCollection) as PermissionScopeType[]

  if (entityIdsCollectionKeys && entityIdsCollectionKeys.length) {
    permissionResolutionInput = entityIdsCollectionKeys.reduce((acc, key) => {
      let permissionResolutionInputs: PermissionResolutionInput[] = acc
      const scopesIds = entityIdsCollection[key]
      if (scopesIds && scopesIds.length) {
        const permissionResolutions = scopesIds.reduce((acc, scopeId) => {
          let permissionInput = acc
          if (scopeId) {
            permissionInput = [
              ...permissionInput,
              {
                permission_name: permissionName,
                scope: {
                  id: scopeId,
                  type: key,
                },
              },
            ]
          }
          return permissionInput
        }, [] as PermissionResolutionInput[])

        permissionResolutionInputs = [...permissionResolutionInputs, ...permissionResolutions]
      }

      permissionResolutionInputs = getPermissionsInputsForEveryAssignee(permissionResolutionInputs, assignees)

      return [...permissionResolutionInputs]
    }, [] as PermissionResolutionInput[])
    // For introducer org, there is no scope
  } else if (permissionName && PermissionAssignmentNamesWithoutScope.includes(permissionName)) {
    permissionResolutionInput = getPermissionsInputsForEveryAssignee(
      [
        {
          permission_name: permissionName,
          scope: {},
        },
      ],
      assignees,
    )
  }

  return permissionResolutionInput
}

export const canMergeArrays = (arrayToCheck: object[][]) => {
  const length = arrayToCheck[0]?.length

  if (
    !length ||
    arrayToCheck.length <= 1 ||
    arrayToCheck.some(
      (element) => !element.length || element.length !== length || element.some((item) => typeof item !== 'object'),
    )
  ) {
    return false
  }

  return true
}

// this function should merge arrays fo the same length
export const mergeObjectArrays = (...args: object[][]): ResolvedPermissionResolution[] => {
  if (!canMergeArrays(args)) {
    console.error('Permissions: Arrays of different dimensions cannot be merged')
    return []
  }

  const merged = [...args[0]] as ResolvedPermissionResolution[]

  for (let i = 1; i < args.length; i++) {
    const thisArray = args[i]

    if (!Array.isArray(thisArray)) {
      console.error('Permissions: Only arrays can be merged')
      return []
    }

    if (!thisArray.every((element) => typeof element === 'object' && element.constructor === Object)) {
      console.error('Permissions: Only arrays of objects can be merged')
      return []
    }

    for (let j = 0; j < thisArray.length; j++) {
      const element = thisArray[j]

      merged[j] = {
        ...merged[j],
        ...element,
      }
    }
  }

  return merged
}

// transformPermissionInputs iteratively creates an array of permission resolution requests
// and creates an array of frontend gates to be combined with the responses and provided as render props
export const transformPermissionInputs = ({ inputs }: { inputs: PermissionInputWithAssignees[] }) => {
  let allGates: { gate: PermissionGateName }[] = []
  const resolved = (inputs || []).reduce((acc, input) => {
    const { entityIdsCollection, gates, assignees } = input
    const resolvedPermissions = gates.reduce((acc, gate) => {
      let builtPermissions = acc
      const permission = permissionBuilder({
        gate,
        entityIdsCollection,
        assignees,
      })

      if (permission && permission.length) {
        // collect gates to be checked against permissions input
        // and returned
        allGates = [...allGates, ...Array(permission.length).fill({ gate })]
        builtPermissions = [...builtPermissions, ...permission]
      }
      return builtPermissions
    }, [] as PermissionResolutionInput[])

    return [...acc, ...resolvedPermissions]
  }, [] as PermissionResolutionInput[])

  return {
    permissionInputs: resolved,
    allGates,
  }
}

// permissionBuilder first translates frontend gate names to backend permission names
// it then generates permission resolution inputs for the permission_resolution_requests
// post request
export const permissionBuilder = memoize(
  ({ gate, entityIdsCollection, assignees }: PermissionBuilderProps): PermissionResolutionInput[] => {
    let result: PermissionResolutionInput[] = []

    const permissionName = GATE_TO_PERMISSION[gate] ?? null

    if (permissionName) {
      result = generatePermissionResolutionInput({
        entityIdsCollection,
        permissionName,
        assignees,
      })
    }
    return result
  },
  ({ gate, entityIdsCollection, assignees }) => JSON.stringify({ gate, entityIdsCollection, assignees }),
)
